/**
 * Tüm AI araçlarının TEK kaynağı (single source of truth).
 *
 * Bu dosya hem React uygulaması (src/data/tools.ts üzerinden, tipli),
 * hem sitemap üreticisi (scripts/generate-sitemap.mjs), hem de prerender
 * scripti (scripts/prerender-tools.mjs) tarafından kullanılır.
 *
 * Yeni bir araç eklemek için sadece buraya bir nesne ekleyin; rotalar,
 * sitemap ve statik SEO sayfaları otomatik olarak senkron kalır.
 *
 * Alanlar:
 *  - kind: 'ai' (Gemini Lambda'ya gider) | 'deterministic' (client-side hesap)
 *  - model: 'gemini-3.1-flash-lite' (sadece kind:'ai')
 *  - icon: lucide-react ikon adı (src/data/tools.ts içindeki haritada eşleşir)
 *  - inputs: { name, label, type:'text'|'textarea'|'select', placeholder, options?, optionLabels?, required? }
 */

import {
  PROJECT_COST_PROFILE_LABEL,
  PROJECT_COST_PROFILE_ORDER,
  formatProjectCostBandLine,
} from '../lib/projectCostBands.shared.js';

/**
 * Sitemap lastmod for tools without their own `contentUpdated` (last date the shared tool copy
 * changed for every tool). Per-tool edits bump only that tool's `contentUpdated`.
 */
export const TOOLS_CONTENT_BASELINE_LASTMOD = '2026-10-30';

/** @typedef {import('./tools').ToolDefinition} ToolDefinition */

/** @type {ToolDefinition[]} */
export const toolsData = [
  {
    id: 'marketplace-product-description',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'content',
    segment: 'E-ticaret satıcıları',
    icon: 'ShoppingBag',
    title: 'Pazaryeri Ürün Açıklaması & SEO Üretici',
    tagline: 'Trendyol, Hepsiburada, Etsy için satış odaklı metin',
    intro:
      'Ürününüzü girin, pazaryerine özel, SEO uyumlu ve dönüşüm getiren ürün açıklamaları saniyeler içinde hazır olsun. Trendyol, Hepsiburada, Amazon ve Etsy formatlarına uygun çıktı üretir.',
    inputs: [
      { name: 'product', label: 'Ürün adı', type: 'text', placeholder: 'Örn: Pamuklu oversize tişört', required: true },
      { name: 'features', label: 'Öne çıkan özellikler', type: 'textarea', placeholder: 'Örn: %100 pamuk, unisex, 5 renk, ön baskı' },
      { name: 'marketplace', label: 'Pazaryeri', type: 'select', options: ['Trendyol', 'Hepsiburada', 'Amazon', 'Etsy'] },
    ],
    seo: {
      title: 'Trendyol Ürün Açıklaması Üretici — Ücretsiz AI Aracı',
      description: 'Trendyol, Hepsiburada ve Etsy için SEO uyumlu, satış getiren ürün açıklamalarını ücretsiz AI ile saniyeler içinde oluşturun.',
      keywords: ['trendyol ürün açıklaması', 'hepsiburada ürün metni', 'e-ticaret ürün açıklaması yazma', 'etsy ürün açıklaması'],
    },
    howItWorks: [
      'Ürün adını ve öne çıkan özelliklerini yazın.',
      'Satış yaptığınız pazaryerini seçin.',
      'Pazaryerine özel 3 farklı açıklama ve anahtar kelime önerisi alın.',
    ],
    useCases: [
      'Yeni ürün listelerken hızlı, profesyonel açıklama',
      'Mevcut ilanları SEO için yenileme',
      'Çok sayıda ürünü kısa sürede metinlendirme',
    ],
    faq: [
      { q: 'Açıklamalar gerçekten SEO uyumlu mu?', a: 'Evet, her çıktıda pazaryeri aramalarında öne çıkmanızı sağlayacak anahtar kelime önerileri de yer alır.' },
      { q: 'Hangi pazaryerlerini destekliyor?', a: 'Trendyol, Hepsiburada, Amazon ve Etsy için optimize edilmiş formatlar üretir.' },
      { q: 'Ücretsiz mi?', a: 'Evet, araç tamamen ücretsizdir ve kayıt gerektirmez.' },
    ],
    example: {
      summary: 'Pamuklu oversize tişört · Trendyol',
      results: [
        '%100 Pamuk Oversize Tişört — Günlük Şıklığın Yeni Adı\nNefes alan pamuklu kumaşıyla gün boyu konfor sunar. Unisex kalıbı ve 5 farklı renk seçeneğiyle her kombine uyum sağlar.\n• %100 pamuk, ter emici kumaş\n• Unisex oversize kalıp\n• 5 renk seçeneği\n• Solmayan ön baskı\nAnahtar kelimeler: oversize tişört, pamuklu tişört, unisex tişört',
      ],
    },
    sitemapPriority: '0.85',
  },

  {
    id: 'google-review-responder',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'local',
    segment: 'Yerel işletmeler & KOBİ',
    icon: 'Star',
    title: 'Google Yorum Yanıtı Üretici',
    tagline: 'Müşteri yorumlarına profesyonel, içten yanıtlar',
    intro:
      'Google İşletme Profilinize gelen olumlu ya da olumsuz yorumlara saniyeler içinde profesyonel, güven veren yanıtlar üretin. İşletmenizin itibarını yorum yönetimiyle güçlendirin.',
    inputs: [
      { name: 'business', label: 'İşletme adı / türü', type: 'text', placeholder: 'Örn: Lavanta Kafe', required: true },
      { name: 'review', label: 'Müşteri yorumu', type: 'textarea', placeholder: 'Gelen yorumu buraya yapıştırın...', required: true },
      { name: 'tone', label: 'Ton', type: 'select', options: ['Samimi ve profesyonel', 'Resmi', 'Sıcak ve teşekkür dolu', 'Çözüm odaklı'] },
    ],
    seo: {
      title: 'Google Yorum Yanıtı Üretici — Müşteri Yorumuna Cevap',
      description: 'Google İşletme yorumlarına profesyonel ve içten yanıtları ücretsiz AI ile oluşturun. Olumlu ve olumsuz yorumlar için hazır cevaplar.',
      keywords: ['google yorum yanıtı', 'müşteri yorumuna cevap', 'google işletme profili yorum', 'olumsuz yoruma cevap'],
    },
    howItWorks: [
      'İşletme adınızı ve gelen yorumu girin.',
      'İstediğiniz yanıt tonunu seçin.',
      'Kopyalayıp kullanabileceğiniz 3 farklı yanıt alın.',
    ],
    useCases: [
      'Olumsuz yorumları profesyonelce yanıtlama',
      'Olumlu yorumlara kişisel teşekkür',
      'Yorum yönetimini hızlandırma',
    ],
    faq: [
      { q: 'Olumsuz yorumlara nasıl yanıt veriyor?', a: 'Savunmacı olmadan, sorunu sahiplenen ve çözüm sunan, itibarınızı koruyan yanıtlar üretir.' },
      { q: 'Yanıtları düzenleyebilir miyim?', a: 'Elbette, üretilen metinleri kopyalayıp dilediğiniz gibi düzenleyebilirsiniz.' },
    ],
    example: {
      summary: 'Lavanta Kafe · olumsuz yorum',
      results: [
        'Değerli geri bildiriminiz için teşekkür ederiz. Yaşadığınız olumsuz deneyim için içtenlikle özür dileriz; bu kesinlikle sunmak istediğimiz hizmet standardı değil. Konuyu ekibimizle değerlendirip gerekli adımları atacağız. Sizi tekrar ağırlayıp telafi etme fırsatı bulmayı çok isteriz.',
      ],
    },
    sitemapPriority: '0.85',
  },

  {
    id: 'menu-description-generator',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'local',
    segment: 'Restoran & kafeler',
    icon: 'UtensilsCrossed',
    title: 'AI Menü Açıklaması Üretici',
    tagline: 'İştah açan menü metinleri + İngilizce çeviri',
    intro:
      'Yemeklerinizi cazip kılan, iştah açıcı menü açıklamaları üretin. Her açıklamanın İngilizce çevirisiyle birlikte, turistik müşterilere de hitap edin.',
    inputs: [
      { name: 'dish', label: 'Yemek / içecek adı', type: 'text', placeholder: 'Örn: Trüflü mantarlı risotto', required: true },
      { name: 'ingredients', label: 'İçindekiler', type: 'textarea', placeholder: 'Örn: arborio pirinci, mantar, trüf yağı, parmesan' },
    ],
    seo: {
      title: 'Menü Açıklaması Yazma — Ücretsiz AI Menü Metni Üretici',
      description: 'Restoran ve kafeler için iştah açıcı menü açıklamalarını İngilizce çevirisiyle ücretsiz AI ile oluşturun.',
      keywords: ['menü açıklaması yazma', 'restoran menü metni', 'menü yazısı örnekleri', 'iştah açıcı menü'],
    },
    howItWorks: [
      'Yemeğin adını ve içindekileri yazın.',
      'Üret butonuna basın.',
      'İştah açan 3 açıklama ve İngilizce çevirilerini alın.',
    ],
    useCases: ['Yeni menü hazırlama', 'Menüyü İngilizceye çevirme', 'QR menü içeriği'],
    faq: [
      { q: 'İngilizce çeviri otomatik mi?', a: 'Evet, her açıklamanın altında İngilizce karşılığı yer alır.' },
      { q: 'Kaç açıklama üretiyor?', a: 'Her seferinde 3 farklı stil alternatifi sunar.' },
    ],
    example: {
      summary: 'Trüflü mantarlı risotto',
      results: [
        'Kremamsı arborio pirinci, taze mantarlar ve gerçek trüf yağının buluştuğu, parmesanla taçlanmış İtalyan klasiği. İlk kaşıkta sizi alıp İtalya\'nın kalbine götürür.\n(Creamy arborio rice with fresh mushrooms and real truffle oil, crowned with parmesan — an Italian classic that whisks you straight to Italy.)',
      ],
    },
    sitemapPriority: '0.8',
  },

  {
    id: 'real-estate-listing-writer',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'local',
    segment: 'Emlak danışmanları',
    icon: 'Home',
    title: 'Emlak İlan Metni Üretici',
    tagline: 'Satılığı hızlandıran ilgi çekici ilan metinleri',
    intro:
      'Mülk detaylarını girin, alıcının ilgisini çekecek, konumun ve yaşam tarzının avantajlarını vurgulayan profesyonel emlak ilan metinleri alın.',
    inputs: [
      { name: 'type', label: 'İlan türü', type: 'select', options: ['Satılık Daire', 'Kiralık Daire', 'Satılık Villa', 'Satılık Arsa', 'İşyeri'] },
      { name: 'location', label: 'Konum', type: 'text', placeholder: 'Örn: Kadıköy, Moda', required: true },
      { name: 'details', label: 'Detaylar', type: 'textarea', placeholder: 'Örn: 3+1, 140 m², 2. kat, deniz manzaralı, yeni bina', required: true },
    ],
    seo: {
      title: 'Emlak İlan Açıklaması Üretici — Ücretsiz AI İlan Metni',
      description: 'Satılık ve kiralık mülkler için ilgi çekici emlak ilan metinlerini ücretsiz AI ile oluşturun. Konum ve yaşam avantajlarını vurgular.',
      keywords: ['emlak ilan açıklaması', 'emlak ilan metni', 'satılık daire ilan örneği', 'emlak ilan yazma'],
    },
    howItWorks: [
      'İlan türünü ve konumu seçin.',
      'Mülk detaylarını yazın.',
      'Yayına hazır 3 ilan metni alın.',
    ],
    useCases: ['Sahibinden / portal ilanları', 'Sosyal medya paylaşımları', 'Vitrin sunumları'],
    faq: [
      { q: 'İlanlar hangi sitelere uygun?', a: 'Sahibinden, Hürriyet Emlak gibi tüm ilan portallarına uygun, genel formatta metinler üretir.' },
      { q: 'Abartılı vaatler içerir mi?', a: 'Hayır, güven veren ama gerçekçi bir dil kullanır.' },
    ],
    example: {
      summary: 'Satılık Daire · Kadıköy Moda · 3+1',
      results: [
        "Moda'nın Kalbinde Deniz Manzaralı 3+1\nKadıköy Moda'da, sahile yürüme mesafesinde, ışıl ışıl bir yaşam sizi bekliyor. 140 m² ferah kullanım alanı, geniş balkonu ve deniz manzarasıyla bu daire hem huzurun hem de şehrin enerjisinin tam ortasında. Kafeler, sahil ve metro birkaç adım ötede.",
      ],
    },
    sitemapPriority: '0.8',
  },

  {
    id: 'pitch-evaluator',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'business',
    segment: 'Startup kurucuları',
    icon: 'Rocket',
    title: 'AI Yatırımcı Pitch Değerlendirici',
    tagline: 'Fikrinizi bir VC gibi puanlayan dürüst geri bildirim',
    intro:
      'Girişim fikrinizi bir risk sermayesi yatırımcısı gözüyle değerlendirin. 100 üzerinden puan, güçlü ve zayıf yönler, yatırımcının soracağı zor sorular ve geliştirilmiş bir pitch alın.',
    inputs: [
      { name: 'idea', label: 'Girişim fikriniz', type: 'textarea', placeholder: 'Fikrinizi 2-3 cümleyle anlatın...', required: true },
      { name: 'market', label: 'Hedef kitle / pazar', type: 'text', placeholder: 'Örn: KOBİ\'ler için muhasebe otomasyonu' },
    ],
    seo: {
      title: 'Startup Fikri Değerlendirme — AI Yatırımcı Pitch Aracı',
      description: 'Girişim fikrinizi bir VC gibi değerlendiren ücretsiz AI aracı. Puan, güçlü/zayıf yönler ve yatırımcı sorularıyla pitch\'inizi güçlendirin.',
      keywords: ['startup fikri değerlendirme', 'yatırımcı sunumu', 'pitch değerlendirme', 'girişim fikri analizi'],
    },
    howItWorks: [
      'Girişim fikrinizi ve hedef pazarınızı yazın.',
      'AI yatırımcı analizini başlatın.',
      'Puan, güçlü/zayıf yönler, zor sorular ve geliştirilmiş pitch alın.',
    ],
    useCases: ['Yatırımcı görüşmesine hazırlık', 'Fikir doğrulama', 'Pitch deck öncesi feedback'],
    faq: [
      { q: 'Değerlendirme ne kadar gerçekçi?', a: 'Deneyimli bir VC perspektifiyle, hem güçlü yönleri hem de riskleri açık sözlü biçimde ortaya koyar.' },
      { q: 'Fikrim güvende mi?', a: 'Girdiğiniz metin saklanmaz; yalnızca anlık analiz için kullanılır.' },
    ],
    example: {
      summary: "KOBİ'ler için AI muhasebe asistanı",
      results: [
        'Skor: 72/100 — Net bir acı noktasına hitap eden, ölçeklenebilir potansiyeli olan sağlam bir fikir.',
        'Güçlü Yönler\n• Büyük ve ödeme yapmaya istekli bir pazar (KOBİ\'ler)\n• Tekrarlayan gelir modeline uygun\n• AI ile gerçek zaman tasarrufu vaadi',
      ],
    },
    sitemapPriority: '0.9',
  },

  {
    id: 'aso-description-generator',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'content',
    segment: 'App & SaaS kurucuları',
    icon: 'Smartphone',
    title: 'App Store & Play Store Açıklama (ASO) Üretici',
    tagline: 'İndirilme getiren mağaza metinleri',
    intro:
      'Uygulamanızı anlatın, App Store ve Google Play için ASO uyumlu başlık, kısa açıklama ve anahtar kelime açısından zengin uzun açıklama alın. Daha fazla görünürlük, daha fazla indirme.',
    inputs: [
      { name: 'app', label: 'Uygulama adı', type: 'text', placeholder: 'Örn: FitTakip', required: true },
      { name: 'description', label: 'Uygulama ne işe yarıyor?', type: 'textarea', placeholder: 'Örn: Antrenman ve beslenme takibi yapan fitness uygulaması', required: true },
      { name: 'platform', label: 'Platform', type: 'select', options: ['Her ikisi', 'App Store', 'Google Play'] },
    ],
    seo: {
      title: 'App Store Açıklaması & ASO Metni Üretici — Ücretsiz',
      description: 'App Store ve Google Play için ASO uyumlu başlık, açıklama ve anahtar kelimeleri ücretsiz AI ile oluşturun. Daha fazla indirme alın.',
      keywords: ['app store açıklaması', 'aso metni', 'play store açıklama', 'uygulama mağaza optimizasyonu'],
    },
    howItWorks: [
      'Uygulama adını ve ne işe yaradığını girin.',
      'Hedef platformu seçin.',
      'Başlık, kısa ve uzun açıklama + anahtar kelimeleri alın.',
    ],
    useCases: ['Yeni uygulama yayınlama', 'ASO ile sıralama yükseltme', 'Mağaza A/B testi metinleri'],
    faq: [
      { q: 'Karakter limitlerine uyuyor mu?', a: 'Evet, başlık ve kısa açıklama mağaza limitlerine göre üretilir.' },
      { q: 'İki platform için de uygun mu?', a: 'App Store, Google Play veya her ikisi için ayrı ayrı optimize edebilirsiniz.' },
    ],
    example: {
      summary: 'FitTakip · Her ikisi',
      results: [
        'Başlık önerileri\n• FitTakip: Antrenman & Beslenme\n• FitTakip — Fitness Takibi\n• FitTakip: Spor Günlüğün',
      ],
    },
    sitemapPriority: '0.8',
  },

  {
    id: 'reels-script-generator',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'content',
    segment: 'Sosyal medya yöneticileri',
    icon: 'Clapperboard',
    title: 'Reels & TikTok Senaryo Üretici',
    tagline: 'Viral hook + 30 saniyelik senaryo + hashtag',
    intro:
      'Konunuzu girin, ilk 3 saniyede izleyiciyi durduracak hook önerileri, sahne sahne 30 saniyelik senaryo ve hazır hashtag setini alın.',
    inputs: [
      { name: 'topic', label: 'Video konusu', type: 'text', placeholder: 'Örn: Evde kahve demleme ipuçları', required: true },
      { name: 'platform', label: 'Platform', type: 'select', options: ['Instagram Reels', 'TikTok', 'YouTube Shorts'] },
      { name: 'audience', label: 'Hedef kitle', type: 'text', placeholder: 'Örn: 20-30 yaş kahve severler' },
    ],
    seo: {
      title: 'Reels Senaryosu & TikTok Hook Üretici — Ücretsiz AI',
      description: 'Instagram Reels ve TikTok için viral hook, 30 saniyelik senaryo ve hashtag önerilerini ücretsiz AI ile oluşturun.',
      keywords: ['reels senaryosu', 'tiktok hook fikirleri', 'reels fikirleri', 'sosyal medya senaryo'],
    },
    howItWorks: [
      'Video konunuzu ve platformu girin.',
      'Hedef kitlenizi belirtin.',
      'Hook, senaryo ve hashtag setini alın.',
    ],
    useCases: ['İçerik takvimi doldurma', 'Viral video fikirleri', 'Marka kampanya senaryosu'],
    faq: [
      { q: 'Hook neden önemli?', a: 'İlk 3 saniye izlenme oranını belirler; araç bu yüzden 5 farklı güçlü açılış sunar.' },
      { q: 'Hashtagler güncel mi?', a: 'Konunuza ve platforma uygun, erişim odaklı hashtag setleri üretir.' },
    ],
    example: {
      summary: 'Evde kahve demleme · Instagram Reels',
      results: [
        'Hook önerileri\n• "Baristaların söylemediği o tek numara…"\n• "Kahveni boşa harcıyorsun, işte kanıtı 👇"\n• "3 saniyede anlayacaksın neyi yanlış yaptığını"\n• "Bunu öğrenmeden bir daha kahve demleme"\n• "Evde barista kalitesi mümkün mü? Deneyelim."',
      ],
    },
    sitemapPriority: '0.8',
  },

  {
    id: 'linkedin-about-generator',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'career',
    segment: 'İş arayanlar & profesyoneller',
    icon: 'Briefcase',
    title: "LinkedIn 'Hakkında' & Niyet Mektubu Üretici",
    tagline: 'Fark yaratan profil özeti ve başvuru metni',
    intro:
      'Rolünüzü ve deneyimlerinizi girin, işe alımcıların dikkatini çeken etkileyici bir LinkedIn "Hakkında" bölümü ve role özel niyet mektubu alın.',
    inputs: [
      { name: 'role', label: 'Rol / unvan', type: 'text', placeholder: 'Örn: Dijital Pazarlama Uzmanı', required: true },
      { name: 'experience', label: 'Deneyim & beceriler', type: 'textarea', placeholder: 'Örn: 5 yıl SEO, Google Ads, içerik stratejisi, ekip yönetimi', required: true },
    ],
    seo: {
      title: "LinkedIn Hakkında Yazma — Ücretsiz AI Profil & Niyet Mektubu",
      description: 'LinkedIn "Hakkında" bölümü ve niyet mektubunu ücretsiz AI ile yazın. İşe alımcıların dikkatini çeken profesyonel metinler.',
      keywords: ['linkedin hakkında yazma', 'niyet mektubu örneği', 'linkedin profil metni', 'cover letter türkçe'],
    },
    howItWorks: [
      'Hedeflediğiniz rolü yazın.',
      'Deneyim ve becerilerinizi ekleyin.',
      'LinkedIn özeti ve niyet mektubunuzu alın.',
    ],
    useCases: ['Yeni iş arayışı', 'LinkedIn profili güncelleme', 'Başvuru kapak metni'],
    faq: [
      { q: 'Birinci tekil şahıs mı yazıyor?', a: 'Evet, "Hakkında" bölümü doğal, samimi ve birinci tekil şahısla yazılır.' },
      { q: 'İngilizce versiyon var mı?', a: 'İhtiyaca göre İngilizce uyarlamalar da üretebilir.' },
    ],
    example: {
      summary: 'Dijital Pazarlama Uzmanı · 5 yıl',
      results: [
        'Markaları dijitalde büyütmeye tutkuyla bağlı bir pazarlamacıyım. Son 5 yılda SEO, Google Ads ve içerik stratejisiyle organik trafiği ortalama %120 artıran kampanyalar yönettim. Veriyi yaratıcılıkla birleştirip ölçülebilir sonuçlar üretmeyi seviyorum.',
      ],
    },
    sitemapPriority: '0.8',
  },

  {
    id: 'brand-kit-generator',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'design',
    segment: 'Marka sahipleri & girişimciler',
    icon: 'Sparkles',
    title: 'AI Marka Kiti Üretici',
    tagline: 'İsim + slogan + renk + ton + domain bir arada',
    intro:
      'İşinizi anlatın, eksiksiz bir marka başlangıç kiti alın: akılda kalıcı isim önerileri, slogan, renk paleti, marka sesi ve uygun domain fikirleri.',
    inputs: [
      { name: 'business', label: 'İş tanımı', type: 'textarea', placeholder: 'Örn: el yapımı doğal sabun üreten bir marka', required: true },
      { name: 'vibe', label: 'Marka hissi / değerler', type: 'text', placeholder: 'Örn: doğal, minimal, premium' },
    ],
    seo: {
      title: 'Marka İsmi Bulma & Marka Kiti Üretici — Ücretsiz AI',
      description: 'İsim, slogan, renk paleti, marka sesi ve domain önerilerini içeren eksiksiz marka kitini ücretsiz AI ile oluşturun.',
      keywords: ['marka ismi bulma', 'marka kimliği oluşturma', 'işletme ismi önerisi', 'slogan üretici'],
    },
    howItWorks: [
      'İşinizi ve marka hissinizi tanımlayın.',
      'Üret butonuna basın.',
      'İsim, slogan, renk, ton ve domain önerilerini alın.',
    ],
    useCases: ['Yeni marka kurma', 'Rebranding fikirleri', 'Pazarlama materyali başlangıcı'],
    faq: [
      { q: 'Renk paleti HEX kodlu mu?', a: 'Evet, kullanıma hazır 4 renkten oluşan HEX kodlu bir palet üretir.' },
      { q: 'Domainler müsait mi?', a: 'Domain fikirleri sunar; müsaitlik kontrolünü bir kayıt sitesinden yapmanız önerilir.' },
    ],
    example: {
      summary: 'El yapımı doğal sabun markası · doğal, minimal',
      results: [
        'Marka isimleri\n• Saf & Sade — sadeliği ve saflığı vurgular\n• Lavé — Fransızca "yıkanmış", zarif ve akılda kalıcı\n• Toprak Sabun — doğallık ve samimiyet\n• Pure Atölye — el yapımı premium hissi\n• Köpük & Doğa — net ve betimleyici',
      ],
    },
    sitemapPriority: '0.85',
  },

  {
    id: 'customer-reply-generator',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'business',
    segment: 'Tüm işletmeler',
    icon: 'MessageSquare',
    title: 'Müşteri Mesajı Yanıt Üretici',
    tagline: 'WhatsApp ve e-postaya hızlı profesyonel yanıt',
    intro:
      'Gelen müşteri mesajını yapıştırın, amacına uygun resmi, samimi ve kısa-net üç farklı yanıt alın. Müşteri iletişimini hızlandırın, ton tutarlılığını koruyun.',
    inputs: [
      { name: 'message', label: 'Gelen mesaj', type: 'textarea', placeholder: 'Müşterinin mesajını buraya yapıştırın...', required: true },
      { name: 'intent', label: 'Mesaj amacı', type: 'select', options: ['Genel soru', 'Satış / fiyat', 'Şikayet', 'Randevu / rezervasyon', 'İade / değişim'] },
      { name: 'channel', label: 'Kanal', type: 'select', options: ['WhatsApp', 'E-posta', 'Instagram DM'] },
    ],
    seo: {
      title: 'Müşteri Mesajına Cevap Üretici — Ücretsiz AI Yanıt Aracı',
      description: 'WhatsApp ve e-posta müşteri mesajlarına profesyonel yanıtları ücretsiz AI ile oluşturun. Resmi, samimi ve kısa-net seçenekler.',
      keywords: ['müşteri mesajına cevap', 'whatsapp işletme mesajı', 'müşteri e-postası yanıtı', 'müşteri iletişimi'],
    },
    howItWorks: [
      'Gelen mesajı yapıştırın.',
      'Mesajın amacını ve kanalı seçin.',
      'Üç farklı tonda yanıt alın.',
    ],
    useCases: ['Hızlı müşteri desteği', 'Satış sorularını yanıtlama', 'Şikayet yönetimi'],
    faq: [
      { q: 'Kaç ton seçeneği var?', a: 'Her mesaj için resmi, samimi ve kısa-net olmak üzere 3 yanıt üretir.' },
      { q: 'Şikayetlerde nasıl davranır?', a: 'Empati kuran, sorunu sahiplenen ve çözüm sunan bir dil kullanır.' },
    ],
    example: {
      summary: 'Satış / fiyat sorusu · WhatsApp',
      results: [
        'Resmi: Merhaba, ilginiz için teşekkür ederiz. Ürünümüzün güncel fiyatı ve stok durumu hakkında detaylı bilgiyi sizinle hemen paylaşıyoruz. Başka bir sorunuz olursa memnuniyetle yardımcı oluruz.',
      ],
    },
    sitemapPriority: '0.8',
  },

  {
    id: 'roi-calculator',
    kind: 'deterministic',
    category: 'business',
    segment: 'Pazarlama & işletme sahipleri',
    icon: 'TrendingUp',
    title: 'Dijital Pazarlama ROI Hesaplayıcı',
    tagline: 'Bütçenizin tahmini getirisini anında görün',
    intro:
      'Aylık pazarlama bütçenizi girin, farklı kanallar için tahmini yatırım getirisini (ROI) anında hesaplayın. Bütçenizi veriye dayalı dağıtın.',
    inputs: [
      { name: 'budget', label: 'Aylık bütçe (TL)', type: 'text', placeholder: 'Örn: 25000', required: true },
    ],
    seo: {
      title: 'Dijital Pazarlama ROI Hesaplayıcı — Ücretsiz',
      description: 'Dijital pazarlama bütçenizin tahmini getirisini ücretsiz ROI hesaplayıcı ile öğrenin. SEO, sosyal medya ve e-posta getirileri.',
      keywords: ['roi hesaplama', 'dijital pazarlama getirisi', 'pazarlama bütçesi hesaplama', 'roas hesaplama'],
    },
    howItWorks: [
      'Aylık pazarlama bütçenizi girin.',
      'Hesapla butonuna basın.',
      'Kanal bazında tahmini getiriyi görün.',
    ],
    useCases: ['Bütçe planlama', 'Kanal karşılaştırma', 'Yatırım kararı'],
    faq: [
      { q: 'Hesaplama nasıl yapılıyor?', a: 'Sektör ortalaması dönüşüm oranlarına dayalı tahmini değerler üretir; gerçek sonuçlar değişebilir.' },
      { q: 'Veri saklanıyor mu?', a: 'Hayır, tüm hesaplama tarayıcınızda yapılır.' },
    ],
    example: {
      summary: 'Aylık 25.000 TL bütçe',
      results: [
        'E-posta pazarlama ROI: ₺105.000 — En yüksek getiri oranı',
        'Sosyal medya reklam getirisi: ₺52.500 — ROAS: 2.1x',
      ],
    },
    sitemapPriority: '0.7',
  },

  {
    id: 'project-cost-estimator',
    contentUpdated: '2026-11-02',
    kind: 'deterministic',
    category: 'business',
    segment: 'Yazılım & ürün ekipleri',
    icon: 'DollarSign',
    title: 'Yazılım Proje Maliyet Tahmincisi',
    tagline: 'Projenizin tahmini bütçesini saniyede öğrenin',
    intro:
      'Proje türünü girin, MVP\'den full-stack platforma kadar tahmini geliştirme maliyeti ve süresini anında görün.',
    inputs: [
      { name: 'project', label: 'Proje türü', type: 'text', placeholder: 'Örn: kurumsal web sitesi, e-ticaret, SaaS, mobil uygulama', required: true },
      {
        name: 'profile',
        label: 'Proje profili',
        type: 'select',
        options: ['auto', ...PROJECT_COST_PROFILE_ORDER],
        optionLabels: [
          'Tarifimden tahmin et',
          ...PROJECT_COST_PROFILE_ORDER.map((id) => PROJECT_COST_PROFILE_LABEL[id].tr),
        ],
      },
    ],
    seo: {
      title: 'Yazılım Proje Maliyet Tahmincisi — Ücretsiz Bütçe Aracı',
      description: 'Yazılım projenizin tahmini maliyetini ücretsiz öğrenin. MVP, SaaS, e-ticaret ve mobil proje bütçe ve süre tahminleri.',
      keywords: ['yazılım proje maliyeti', 'mvp maliyeti', 'uygulama yaptırma fiyatları', 'proje bütçe tahmini'],
    },
    howItWorks: [
      'Proje türünüzü yazın; isterseniz proje profilini doğrudan seçin.',
      'Hesapla butonuna basın.',
      'Maliyet ve süre aralıklarını görün; tür tahmin edildiyse profili değiştirerek karşılaştırın.',
    ],
    useCases: ['Bütçe planlama', 'Teklif öncesi tahmin', 'Yatırım hesabı'],
    faq: [
      { q: 'Fiyatlar kesin mi?', a: 'Hayır — premium butik ajans bandlarına dayalı tahmini aralıklardır (TRY, KDV hariç). Kesin fiyat proje bazında hesaplanır: talebinizi dinler, kapsam ve iş listesini çıkarır, eforu hesaplar, projeye uygun outsource ekibi kurar ve fiyatı bu efor/maliyetten hesaplayıp yazılı teklifle iletiriz.' },
      { q: 'Hangi proje türlerini kapsıyor?', a: 'Kurumsal/tanıtım sitesi, SaaS/MVP, mobil ürün, e-ticaret ve genel/özel yazılım (ERP, CRM, panel) profilleri; her biri için MVP, platform, UI/UX ve bakım kalemleri.' },
      { q: '“Kurumsal site” ile “kurumsal web sitesi” farklı fiyat mı çıkarır?', a: 'Hayır. Kurumsal site, kurumsal web sitesi, kurumsal internet sitesi, corporate site ve corporate website aynı profile (kurumsal / tanıtım sitesi) gider. Tarif birden fazla türü içeriyorsa veya tür anlaşılamıyorsa sonuçta bunu belirtir, profili seçmenizi isteriz.' },
    ],
    example: {
      summary: 'SaaS projesi',
      results: [
        formatProjectCostBandLine('saas_mvp', 'mvp', 'tr'),
        formatProjectCostBandLine('saas_mvp', 'platform', 'tr'),
      ],
    },
    sitemapPriority: '0.7',
  },

  {
    id: 'color-palette-generator',
    kind: 'deterministic',
    category: 'design',
    segment: 'Tasarımcılar & marka sahipleri',
    icon: 'Palette',
    title: 'AI Renk Paleti Üretici',
    tagline: 'Marka ruhunuza uygun hazır paletler',
    intro:
      'Markanızın ruhunu tanımlayın (modern, sıcak, doğal…), tasarım projeleriniz için kullanıma hazır, HEX kodlu renk paletleri alın.',
    inputs: [
      { name: 'mood', label: 'Marka ruhu', type: 'text', placeholder: 'Örn: modern, sıcak, minimal', required: true },
    ],
    seo: {
      title: 'Renk Paleti Üretici — Ücretsiz Marka Renkleri Aracı',
      description: 'Markanızın ruhuna uygun HEX kodlu renk paletlerini ücretsiz oluşturun. Tasarım projeleriniz için profesyonel renk önerileri.',
      keywords: ['renk paleti oluşturma', 'marka renkleri', 'hex renk paleti', 'web sitesi renk önerisi'],
    },
    howItWorks: [
      'Marka ruhunuzu bir-iki kelimeyle yazın.',
      'Üret butonuna basın.',
      'HEX kodlu palet seçeneklerini alın.',
    ],
    useCases: ['Web sitesi tasarımı', 'Marka kimliği', 'Sunum & moodboard'],
    faq: [
      { q: 'Paletleri nasıl kullanırım?', a: 'HEX kodlarını kopyalayıp tasarım aracınızda doğrudan kullanabilirsiniz.' },
      { q: 'Kaç palet üretiyor?', a: 'Her seferinde farklı ruh hallerine uygun birkaç palet sunar.' },
    ],
    example: {
      summary: 'modern',
      results: [
        '#0A0A0A • #F5F5F5 • #3B82F6 • #10B981 — minimal & temiz',
        '#0D1117 • #58A6FF • #238636 • #F78166 — teknolojik & modern',
      ],
    },
    sitemapPriority: '0.7',
  },

  {
    id: 'ats-cv-analyzer',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'career',
    segment: 'İş arayanlar & kariyer değiştirenler',
    icon: 'ClipboardCheck',
    title: 'ATS CV Skor & İyileştirme Aracı',
    tagline: 'Özgeçmişinizi ilana göre puanlayın, eksikleri görün',
    intro:
      'CV metninizi ve hedeflediğiniz ilan açıklamasını yapıştırın; ATS uyumluluk skoru, eksik anahtar kelimeler ve madde iyileştirme önerilerini saniyeler içinde alın. Kayıt yok, ücretsiz.',
    inputs: [
      { name: 'cv', label: 'CV / özgeçmiş metni', type: 'textarea', placeholder: 'Deneyim, beceriler ve eğitim bölümlerinizi buraya yapıştırın...', required: true },
      { name: 'jobDescription', label: 'İlan metni (opsiyonel)', type: 'textarea', placeholder: 'Başvurduğunuz pozisyonun ilan açıklamasını yapıştırın — skor daha isabetli olur.' },
    ],
    seo: {
      title: 'ATS CV Kontrol & Puanlama — Ücretsiz Yapay Zeka CV Analizi',
      description: 'CV\'nizi ATS uyumluluğu için ücretsiz analiz edin. Skor, eksik anahtar kelimeler ve iyileştirme önerileri — ilan metnine göre özelleştirilmiş.',
      keywords: ['ats uyumlu cv kontrol', 'yapay zeka cv kontrol', 'cv puanlama', 'ats cv analizi', 'özgeçmiş kontrol'],
    },
    howItWorks: [
      'CV metninizi yapıştırın.',
      'Varsa ilan açıklamasını ekleyin.',
      'Skor, eksikler ve iyileştirilmiş madde önerilerini alın.',
    ],
    useCases: [
      'İş başvurusu öncesi CV kontrolü',
      'İlana özel anahtar kelime optimizasyonu',
      'ATS sistemlerinden geçme şansını artırma',
    ],
    faq: [
      { q: 'Skor neye göre hesaplanıyor?', a: 'İlan metnindeki anahtar kelime eşleşmesi (~%50), rol uyumu, madde formatı ve netlik. Eksik kritik kelimeler skoru düşürür — yapay yükseltme yapılmaz.' },
      { q: 'Skoru yapay olarak yükseltiyor musunuz?', a: 'Hayır. Skor ilan-CV eşleşmesine dayanır; eksik "GA4" gibi kelimeler varsa skor düşük çıkabilir. Bu kasıtlıdır.' },
      { q: 'CV\'m saklanıyor mu?', a: 'Hayır. Metin yalnızca anlık analiz için kullanılır.' },
      { q: 'İlan metni olmadan kullanılabilir mi?', a: 'Evet, genel CV kalitesi analizi alırsınız; ilan metni skoru daha isabetli kılar.' },
    ],
    example: {
      summary: 'Dijital pazarlama uzmanı · SEO ilanı',
      results: [
        'ATS Skoru: 52/100 — İlandaki 10 kritik beceriden 4\'ü CV\'de geçiyor (%40). Eksik: "GA4", "CRO", "A/B test", "SQL".\n\nİyileştirme (yalnızca CV\'nizde bu deneyim varsa):\n• Mevcut Google Ads maddesine GA4 ifadesi eklenebilir — gerçekten kullandıysanız.',
      ],
    },
    sitemapPriority: '0.9',
  },

  {
    id: 'job-application-writer',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'career',
    segment: 'İş arayanlar',
    icon: 'FileText',
    title: 'İlana Özel CV Maddeleri & Ön Yazı Üretici',
    tagline: 'Her başvuruya özel ATS uyumlu metin',
    intro:
      'Hedef rolünüzü, deneyiminizi ve ilan metnini girin; o pozisyona özel CV madde önerileri ve ön yazı taslağı alın. Genel LinkedIn metninden farklı — doğrudan başvuruya hazır.',
    inputs: [
      { name: 'role', label: 'Hedef rol / unvan', type: 'text', placeholder: 'Örn: Senior Frontend Developer', required: true },
      { name: 'experience', label: 'Deneyim & beceriler', type: 'textarea', placeholder: 'Örn: 4 yıl React, TypeScript, ekip liderliği, fintech projeleri', required: true },
      { name: 'jobDescription', label: 'İlan açıklaması', type: 'textarea', placeholder: 'Başvurduğunuz ilanın metnini yapıştırın...', required: true },
    ],
    seo: {
      title: 'Yapay Zeka CV Hazırlama & Ön Yazı — Ücretsiz İlana Özel',
      description: 'İlan metnine göre ATS uyumlu CV maddeleri ve ön yazı üretin. Ücretsiz, kayıtsız — yapay zeka ile cv hazırlama aracı.',
      keywords: ['yapay zeka cv hazırlama', 'ön yazı örnekleri', 'cover letter türkçe', 'ilana özel cv', 'ats uyumlu cv hazırlama'],
    },
    howItWorks: [
      'Hedef rol ve deneyiminizi yazın.',
      'İlan açıklamasını yapıştırın.',
      'CV maddeleri ve ön yazı taslağını kopyalayın.',
    ],
    useCases: [
      'Her ilana özelleştirilmiş başvuru',
      'Ön yazı yazma süresini kısaltma',
      'ATS anahtar kelime eşleştirme',
    ],
    faq: [
      { q: 'LinkedIn Hakkında aracından farkı ne?', a: 'Bu araç belirli bir ilan metnine göre CV maddeleri ve ön yazı üretir; başvuru odaklıdır.' },
      { q: 'Ön yazı hangi dilde gelir?', a: 'Girdiğiniz metnin diline göre — Türkçe veya İngilizce.' },
      { q: 'Ücretsiz mi?', a: 'Evet, kayıt gerektirmez.' },
    ],
    example: {
      summary: 'Senior Frontend · fintech ilanı',
      results: [
        'CV maddesi önerisi:\n• React ve TypeScript ile ödeme akışı arayüzlerini geliştirdim; sayfa yüklenme süresini %40 düşürdüm, dönüşüm kaybını azalttım.\n\nÖn yazı açılışı:\nFintech ürünlerinde performans ve güvenilir arayüzler geliştirme konusundaki deneyimimle ekibinize katkı sağlamak istiyorum.',
      ],
    },
    sitemapPriority: '0.9',
  },

  {
    id: 'presentation-outline-generator',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'business',
    segment: 'Kurucular & ekipler',
    icon: 'Presentation',
    title: 'Sunum & Pitch Deck Taslağı Üretici',
    tagline: 'Slayt başlıkları + konuşma notları hazır',
    intro:
      'Sunum konusunu ve hedef kitlenizi girin; slayt slayt başlık, ana mesaj ve konuşma notlarından oluşan kullanıma hazır bir taslak alın. PowerPoint veya Gamma\'ya yapıştırmaya uygun.',
    inputs: [
      { name: 'topic', label: 'Sunum konusu', type: 'text', placeholder: 'Örn: Q3 büyüme stratejisi / Seed yatırım turu', required: true },
      { name: 'audience', label: 'Hedef kitle', type: 'text', placeholder: 'Örn: yatırımcılar, müşteri ekibi, iç ekip' },
      { name: 'purpose', label: 'Sunum türü', type: 'select', options: ['Yatırımcı pitch', 'Satış sunumu', 'Eğitim / workshop', 'Kurumsal rapor'] },
    ],
    seo: {
      title: 'Sunum Hazırlayan Yapay Zeka — Ücretsiz Pitch Deck Taslağı',
      description: 'Sunum ve pitch deck taslağını yapay zeka ile ücretsiz oluşturun. Slayt başlıkları, konuşma notları — kayıtsız, hemen deneyin.',
      keywords: ['sunum hazırlayan yapay zeka', 'pitch deck taslağı', 'sunum hazırlama ai', 'slayt içeriği üretici'],
    },
    howItWorks: [
      'Sunum konusunu ve kitlenizi tanımlayın.',
      'Sunum türünü seçin.',
      'Slayt taslağı ve konuşma notlarını alın.',
    ],
    useCases: [
      'Yatırımcı görüşmesi hazırlığı',
      'Satış demo sunumu',
      'Ekip içi strateji paylaşımı',
    ],
    faq: [
      { q: 'PowerPoint dosyası üretiyor mu?', a: 'Metin taslak üretir; slayt başlıklarını PowerPoint, Google Slides veya Gamma\'ya yapıştırabilirsiniz.' },
      { q: 'Kaç slayt?', a: 'Genelde 8–12 slaytlık dengeli bir akış sunar.' },
      { q: 'Figtures ile ilişkisi?', a: 'Bu araç ücretsiz bir başlangıç; özel pitch deck tasarımı için ekibimizle çalışabilirsiniz.' },
    ],
    example: {
      summary: 'Seed turu · yatırımcılar',
      results: [
        'Slayt 1 — Hook\n"KOBİ\'ler hâlâ Excel\'de muhasebe yapıyor. Biz bunu 10 dakikada değiştiriyoruz."\n\nSlayt 2 — Problem\n• 2,3M KOBİ manuel süreçte\n• Ortalama 12 saat/hafta kayıp',
      ],
    },
    sitemapPriority: '0.88',
  },

  {
    id: 'youtube-seo-generator',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'content',
    segment: 'YouTuber & içerik üreticileri',
    icon: 'Youtube',
    title: 'YouTube Başlık & Açıklama SEO Üretici',
    tagline: 'Tıklama getiren başlık + arama uyumlu açıklama',
    intro:
      'Video konunuzu girin; tıklama oranı odaklı başlık alternatifleri, SEO uyumlu açıklama metni ve etiket önerileri alın. Reels aracından farklı — YouTube arama ve keşfet için optimize.',
    inputs: [
      { name: 'topic', label: 'Video konusu', type: 'text', placeholder: 'Örn: Evde kahve demleme rehberi', required: true },
      { name: 'niche', label: 'Kanal nişi / hedef kitle', type: 'text', placeholder: 'Örn: kahve, lifestyle, 25-35 yaş' },
      { name: 'videoLength', label: 'Video süresi', type: 'select', options: ['Kısa (Shorts)', 'Orta (5-15 dk)', 'Uzun (15+ dk)'] },
    ],
    seo: {
      title: 'YouTube Başlık Oluşturucu & Açıklama SEO — Ücretsiz AI',
      description: 'YouTube video başlığı, açıklama ve etiket önerilerini ücretsiz AI ile oluşturun. SEO uyumlu, tıklama odaklı — kayıtsız.',
      keywords: ['youtube başlık oluşturucu', 'youtube açıklama yazma', 'youtube seo', 'video başlık önerileri'],
    },
    howItWorks: [
      'Video konusunu yazın.',
      'Nişinizi ve süreyi belirtin.',
      'Başlık, açıklama ve etiket setini alın.',
    ],
    useCases: [
      'Yeni video yayınlama',
      'Eski videoları SEO için yenileme',
      'A/B test için başlık varyasyonları',
    ],
    faq: [
      { q: 'Reels aracından farkı ne?', a: 'Bu araç YouTube arama ve keşfet algoritmasına göre başlık, açıklama ve tag üretir.' },
      { q: 'Karakter limitlerine uyuyor mu?', a: 'Başlıklar 100 karakter civarında tutulur; açıklama yapılandırılmış SEO formatında gelir.' },
      { q: 'Ücretsiz mi?', a: 'Evet.' },
    ],
    example: {
      summary: 'Evde kahve demleme · orta format',
      results: [
        'Başlık önerileri\n• Evde Barista Kahvesi: 3 Hata Yapmayın (5 Dakikada)\n• Filtre Kahve Neden Acı Oluyor? — Çözüm Basit\n• 200 TL\'ye Evde Espresso Kalitesi Mümkün mü?',
      ],
    },
    sitemapPriority: '0.88',
  },

  {
    id: 'instagram-caption-generator',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'content',
    segment: 'Sosyal medya yöneticileri & markalar',
    icon: 'Instagram',
    title: 'Instagram Caption & Hashtag Üretici',
    tagline: 'Gönderi metni + bio + hashtag seti',
    intro:
      'Gönderi konusunu ve marka tonunuzu girin; kaydırmayı durduran caption, opsiyonel bio satırı ve erişim odaklı hashtag seti alın. Türkçe ve İngilizce.',
    inputs: [
      { name: 'topic', label: 'Gönderi konusu', type: 'text', placeholder: 'Örn: Yeni sezon koleksiyonu lansmanı', required: true },
      { name: 'brand', label: 'Marka / hesap türü', type: 'text', placeholder: 'Örn: moda markası, kişisel blog, restoran' },
      { name: 'tone', label: 'Ton', type: 'select', options: ['Samimi', 'Profesyonel', 'Eğlenceli', 'Bilgilendirici'] },
    ],
    seo: {
      title: 'Instagram Caption Generator — Ücretsiz Hashtag & Bio Aracı',
      description: 'Instagram gönderi metni, bio önerisi ve hashtag setini ücretsiz AI ile oluşturun. Kayıtsız, hemen kullanın.',
      keywords: ['instagram caption', 'instagram bio yazma', 'hashtag önerileri', 'instagram metin üretici'],
    },
    howItWorks: [
      'Gönderi konusunu yazın.',
      'Marka türünü ve tonu seçin.',
      'Caption, hashtag ve bio önerisini kopyalayın.',
    ],
    useCases: [
      'Günlük içerik üretimi',
      'Kampanya lansman gönderileri',
      'Bio yenileme',
    ],
    faq: [
      { q: 'Hashtag sayısı kaç?', a: 'Genelde 15–25 arası, konuya uygun karışık erişim ve niş etiketler.' },
      { q: 'Emoji kullanıyor mu?', a: 'Tona göre dengeli emoji ekler; abartılı değil.' },
      { q: 'Reels aracıyla birlikte kullanılır mı?', a: 'Evet — Reels senaryosu + bu araç caption/hashtag için ideal ikili.' },
    ],
    example: {
      summary: 'Sezon lansmanı · moda markası',
      results: [
        'Caption (Samimi):\nYeni sezon kapıda 🖤\nBu koleksiyonu aylarca tasarladık — sade, cesur, gündüzden geceye.\nHangisini ilk siz deneyeceksiniz?\n\nHashtagler:\n#yeniSezon #moda2026 #ootd #stilinle #minimalmoda',
      ],
    },
    sitemapPriority: '0.87',
  },

  {
    id: 'job-posting-writer',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'career',
    segment: 'İK ekipleri & işverenler',
    icon: 'UserSearch',
    title: 'İş İlanı Yazıcı — ATS Uyumlu İlan Metni',
    tagline: 'Kariyer.net / LinkedIn formatında ilan + aday çekiciliği',
    intro:
      'Pozisyon, şirket ve aranan nitelikleri girin; ATS uyumlu iş tanımı, sorumluluklar, aday profili ve dahil edici dil kontrol listesi alın. Genel "iş ilanı yaz" promptundan farklı — platforma yapıştırmaya hazır yapı.',
    inputs: [
      { name: 'role', label: 'Pozisyon / unvan', type: 'text', placeholder: 'Örn: Kıdemli Ürün Müdürü', required: true },
      { name: 'company', label: 'Şirket & sektör', type: 'text', placeholder: 'Örn: B2B SaaS · 45 kişi · İstanbul (hibrit)', required: true },
      { name: 'requirements', label: 'Aranan nitelikler & sorumluluklar', type: 'textarea', placeholder: 'Örn: 5+ yıl ürün yönetimi, B2B deneyimi, roadmap, stakeholder yönetimi, İngilizce', required: true },
      { name: 'employmentType', label: 'Çalışma modeli', type: 'select', options: ['Tam zamanlı', 'Yarı zamanlı', 'Staj', 'Freelance / proje'] },
    ],
    seo: {
      title: 'Yapay Zeka İş İlanı Oluşturma — Ücretsiz ATS Uyumlu İlan Yazıcı',
      description: 'İş ilanı metnini yapay zeka ile ücretsiz oluşturun. ATS uyumlu görev tanımı, aday profili ve dahil edici dil kontrolü — Kariyer.net ve LinkedIn\'e yapıştırmaya hazır.',
      keywords: ['yapay zeka iş ilanı oluşturma', 'iş ilanı yazma aracı', 'ats uyumlu iş ilanı', 'ilan metni oluşturucu', 'ik iş ilanı şablonu'],
    },
    howItWorks: [
      'Pozisyon ve şirket bilgisini girin.',
      'Aranan nitelikleri ve sorumlulukları yazın.',
      'İlan metni, aday profili ve kontrol listesini kopyalayın.',
    ],
    useCases: [
      'KOBİ ve startup işe alım süreçleri',
      'İK ekiplerinin ilan yazım süresini kısaltma',
      'LinkedIn ve Kariyer.net ilanlarını standartlaştırma',
    ],
    faq: [
      { q: 'ChatGPT\'ye "iş ilanı yaz" demekten farkı ne?', a: 'Bu araç ATS anahtar kelime eşlemesi, sorumluluk/nitelik ayrımı, dahil edici dil kontrol listesi ve platforma yapıştırmaya hazır bölümler üretir — tek paragraf değil.' },
      { q: 'Maaş aralığı ekler mi?', a: 'Siz vermediyseniz uydurmaz; ilan sonunda "Maaş aralığı: [ekleyin]" placeholder bırakır.' },
      { q: 'İlan dili nasıl belirlenir?', a: 'Girdiğiniz metnin diline göre — Türkçe veya İngilizce.' },
    ],
    example: {
      summary: 'Kıdemli Ürün Müdürü · B2B SaaS',
      results: [
        'İlan özeti:\n• Sorumluluklar: 6 madde (eylem fiili + ölçülebilir kapsam)\n• Aranan nitelikler: 5 madde (must-have / nice-to-have ayrımı)\n• Dahil edici dil: "5+ yıl" yerine "5 yıl deneyim" ✓\n• ATS anahtar kelimeler: product roadmap, B2B SaaS, stakeholder',
      ],
    },
    sitemapPriority: '0.9',
  },

  {
    id: 'quote-proposal-writer',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'business',
    segment: 'Freelancer & ajanslar',
    icon: 'HandCoins',
    title: 'Fiyat Teklifi & Proje Teklif Mektubu Üretici',
    tagline: 'Kapsam, teslimat, ödeme planı — müşteriye göndermeye hazır',
    intro:
      'Hizmet türünüzü, proje kapsamını ve bütçe aralığını girin; Türk iş düzenine uygun teklif mektubu, kapsam tablosu, hariç tutulanlar ve ödeme planı alın. ChatGPT\'nin genel paragrafından farklı — imzaya yakın yapı.',
    inputs: [
      { name: 'service', label: 'Hizmet / proje türü', type: 'text', placeholder: 'Örn: Kurumsal web sitesi + CMS entegrasyonu', required: true },
      { name: 'scope', label: 'Kapsam detayları', type: 'textarea', placeholder: 'Örn: 12 sayfa, blog modülü, çok dilli, 2 revizyon turu, 8 hafta teslim', required: true },
      { name: 'budget', label: 'Bütçe aralığı (₺)', type: 'text', placeholder: 'Örn: 120.000 – 150.000 TL + KDV', required: true },
      { name: 'clientType', label: 'Müşteri profili', type: 'select', options: ['KOBİ', 'Kurumsal', 'Startup', 'Kamu / STK'] },
    ],
    seo: {
      title: 'Yapay Zeka Fiyat Teklifi Hazırlama — Ücretsiz Proje Teklif Şablonu',
      description: 'Fiyat teklifi ve proje teklif mektubunu yapay zeka ile ücretsiz oluşturun. Kapsam, teslimat, ödeme planı ve hariç tutulanlar — freelancer ve ajanslar için.',
      keywords: ['yapay zeka fiyat teklifi', 'teklif mektubu şablonu', 'proje teklifi hazırlama', 'yapay zeka teklif hazırlama', 'freelance teklif yazma'],
    },
    howItWorks: [
      'Hizmet ve kapsamı detaylandırın.',
      'Bütçe aralığını yazın.',
      'Teklif mektubu, kapsam tablosu ve ödeme planını alın.',
    ],
    useCases: [
      'Freelance proje teklifleri',
      'Ajans satış öncesi teklif hazırlığı',
      'Teklif formatını standartlaştırma',
    ],
    faq: [
      { q: 'Hukuki geçerlilik sağlar m?', a: 'Metin taslağı üretir; imza, KDV ve sözleşme maddelerini kendi muhasebe/hukuk sürecinizle tamamlayın.' },
      { q: 'Bütçe uydurur mu?', a: 'Hayır — yalnızca sizin yazdığınız bütçe aralığını kullanır.' },
      { q: 'Kapsam dışı maddeler gelir mi?', a: 'Evet, "Hariç tutulanlar" bölümü teklifin netliğini artırır.' },
    ],
    example: {
      summary: 'Kurumsal web · KOBİ müşteri',
      results: [
        'Teklif özeti:\n• Kapak + proje özeti (2 paragraf)\n• Kapsam tablosu: 5 satır (teslimat + süre)\n• Toplam: ₺135.000 + KDV (verdiğiniz aralık içinde)\n• Ödeme: %40 başlangıç · %40 ara · %20 teslim\n• Hariç tutulanlar: hosting, stok fotoğraf, 3. parti lisanslar',
      ],
    },
    sitemapPriority: '0.89',
  },

  {
    id: 'motivation-letter-writer',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'career',
    segment: 'Öğrenciler & burs başvuruları',
    icon: 'GraduationCap',
    title: 'Motivasyon Mektubu Üretici — Burs & Yüksek Lisans',
    tagline: 'Programa neden · size neden · katkınız — 3 bölümlü yapı',
    intro:
      'Hedef programı, geçmişinizi ve motivasyonunuzu girin; burs/yüksek lisans başvurularına özel motivasyon mektubu taslağı alın. Ön yazıdan farklı — "neden bu program" ve "topluluğa katkı" çerçevesi zorunlu.',
    inputs: [
      { name: 'program', label: 'Program / burs / okul', type: 'text', placeholder: 'Örn: Fulbright · MIT Media Lab · Erasmus+', required: true },
      { name: 'background', label: 'Geçmişiniz & başarılar', type: 'textarea', placeholder: 'Örn: Bilgisayar mühendisliği mezunu, açık kaynak projeler, sosyal girişim deneyimi', required: true },
      { name: 'motivation', label: 'Neden bu program?', type: 'textarea', placeholder: 'Örn: HCI araştırması, erişilebilir teknoloji, Avrupa ağı', required: true },
      { name: 'tone', label: 'Ton', type: 'select', options: ['Akademik', 'Kişisel & hikâye', 'Kısa & net'] },
    ],
    seo: {
      title: 'Yapay Zeka Motivasyon Mektubu — Ücretsiz Burs Başvuru Taslağı',
      description: 'Motivasyon mektubunu yapay zeka ile ücretsiz yazın. Burs, yüksek lisans ve değişim programı başvuruları için yapılandırılmış taslak — ön yazıdan farklı çerçeve.',
      keywords: ['yapay zeka motivasyon mektubu', 'motivasyon mektubu örneği', 'burs başvuru mektubu', 'motivation letter türkçe', 'yüksek lisans motivasyon'],
    },
    howItWorks: [
      'Program ve geçmişinizi yazın.',
      'Motivasyonunuzu açıklayın.',
      '3 ton varyantında motivasyon mektubu alın.',
    ],
    useCases: [
      'Burs ve hibe başvuruları',
      'Yüksek lisans / doktora başvuruları',
      'Erasmus ve değişim programları',
    ],
    faq: [
      { q: 'Ön yazı aracından farkı ne?', a: 'Motivasyon mektubu "neden bu program", "gelecek hedefi" ve "topluluğa katkı" çerçevesine odaklanır; iş ilanına başvuru değildir.' },
      { q: 'Uydurma başarı ekler mi?', a: 'Hayır — yalnızca sizin yazdığınız geçmiş ve motivasyonu kullanır.' },
      { q: 'İngilizce programlar için?', a: 'Girdi dilinize göre Türkçe veya İngilizce üretir.' },
    ],
    example: {
      summary: 'Erasmus+ · sosyal girişim geçmişi',
      results: [
        'Akademik ton:\nParagraf 1 — Programa neden (spesifik lab/proje referansı)\nParagraf 2 — Geçmiş + kanıtlanmış etki\nParagraf 3 — Program sonrası hedef + host kuruma katkı\n\nKapanış: net, abartısız, 1 cümlelik vizyon',
      ],
    },
    sitemapPriority: '0.89',
  },

  {
    id: 'meta-ads-copy-generator',
    kind: 'ai',
    model: 'gemini-3.1-flash-lite',
    category: 'content',
    segment: 'Dijital pazarlamacılar & e-ticaret',
    icon: 'Megaphone',
    title: 'Meta Reklam Metni Üretici — Facebook & Instagram Ads',
    tagline: 'Primary text + başlık + CTA + hook analizi',
    intro:
      'Ürün/hizmet, hedef kitle ve kampanya hedefini girin; Meta Ads karakter limitlerine uygun reklam metinleri, 3 hook varyantı ve CTA önerileri alın. Genel "reklam yaz" yerine platform limitleri ve yapı zorunlu.',
    inputs: [
      { name: 'product', label: 'Ürün / hizmet', type: 'text', placeholder: 'Örn: Organik cilt bakım seti · %100 doğal içerik', required: true },
      { name: 'audience', label: 'Hedef kitle', type: 'text', placeholder: 'Örn: 25-40 yaş kadın, İstanbul, cilt bakımına ilgili', required: true },
      { name: 'objective', label: 'Kampanya hedefi', type: 'select', options: ['Satış / dönüşüm', 'Trafik', 'Marka bilinirliği', 'Lead toplama'] },
      { name: 'offer', label: 'Teklif / fark (opsiyonel)', type: 'text', placeholder: 'Örn: İlk siparişe %20 indirim, ücretsiz kargo' },
    ],
    seo: {
      title: 'Yapay Zeka Meta Reklam Metni — Ücretsiz Facebook & Instagram Ads Copy',
      description: 'Meta reklam metnini yapay zeka ile ücretsiz oluşturun. Primary text, başlık, açıklama ve CTA — karakter limitlerine uygun, 3 hook varyantı.',
      keywords: ['yapay zeka meta reklam', 'facebook reklam metni', 'instagram ads copy', 'yapay zeka reklam metni', 'meta ads metin üretici'],
    },
    howItWorks: [
      'Ürün ve hedef kitleyi tanımlayın.',
      'Kampanya hedefini seçin.',
      '3 reklam seti + hook analizi alın.',
    ],
    useCases: [
      'E-ticaret kampanya lansmanı',
      'Lead generation reklamları',
      'A/B test için metin varyasyonları',
    ],
    faq: [
      { q: 'Karakter limitlerine uyuyor mu?', a: 'Primary text ~125 karakter öncelik, başlık ~40, açıklama ~30 — Meta Ads Manager sınırlarına göre yapılandırılır.' },
      { q: 'Fiyat uydurur mu?', a: 'Hayır — teklif alanını boş bıraktıysanız indirim veya fiyat icat etmez.' },
      { q: 'Türkçe reklam için uygun mu?', a: 'Evet — Türk tüketici diline uygun, abartısız CTA\'lar üretir.' },
    ],
    example: {
      summary: 'Cilt bakım seti · dönüşüm hedefi',
      results: [
        'Set 1 — Merak hook\nPrimary: "Cildiniz neden kuruyor? Çoğu zaman tek bir hatadan."\nBaşlık: Doğal bakım seti · %20 indirim\nCTA: Hemen keşfet\n\nHook analizi: Soru + problem tanımı — soğuk kitle için uygun',
      ],
    },
    sitemapPriority: '0.88',
  },
];
